import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Star, Quote, Sparkles } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselSwipeHint,
} from "@/components/ui/carousel";
import {
  getPublishedTestimonials,
  type PublicTestimonial,
} from "@/lib/social-proof.functions";
import { LandingFooter } from "@/components/LandingFooter";

const TESTIMONIALS_OG =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/bda540b3-55c7-4373-af6d-b5588ecb7c64";

export const Route = createFileRoute("/testimonials")({
  head: () => ({
    meta: [
      { title: "Community Testimonials — Recovery Trauma™" },
      {
        name: "description",
        content:
          "Real voices from the Recovery Trauma™ community. Instagram, Facebook, and Trustpilot reviews from survivors and supporters.",
      },
      { property: "og:title", content: "Community Testimonials — Recovery Trauma™" },
      {
        property: "og:description",
        content:
          "Instagram, Facebook, and Trustpilot reviews from the Recovery Trauma™ community.",
      },
      { property: "og:image", content: TESTIMONIALS_OG },
      { name: "twitter:image", content: TESTIMONIALS_OG },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:url", content: "https://recoverytrauma.com/testimonials" },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/testimonials" },
    ],
  }),
  component: TestimonialsPage,
});

const SOURCE_TABS: { key: "all" | "Instagram" | "Facebook" | "Trustpilot"; label: string; emoji: string }[] = [
  { key: "all", label: "All", emoji: "💛" },
  { key: "Instagram", label: "Instagram", emoji: "📸" },
  { key: "Facebook", label: "Facebook", emoji: "👍" },
  { key: "Trustpilot", label: "Trustpilot", emoji: "⭐" },
];

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${
            i < rating ? "fill-[var(--gold-deep)] text-[var(--gold-deep)]" : "text-foreground/20"
          }`}
        />
      ))}
    </div>
  );
}

function TestimonialCard({ t }: { t: PublicTestimonial }) {
  return (
    <div className="flex h-full flex-col rounded-[1.75rem] border-2 border-gold/30 bg-gradient-to-br from-card via-gold-light/20 to-card p-6 shadow-sm sm:p-7">
      <Quote className="h-7 w-7 text-[var(--gold-deep)]/70" aria-hidden />
      <StarRow rating={t.rating} />
      <p className="mt-3 flex-1 font-body text-base leading-relaxed text-foreground/90">
        "{t.quote}"
      </p>
      <div className="mt-5 flex items-center gap-3">
        {t.avatar_url ? (
          <img loading="lazy" decoding="async"
            src={t.avatar_url}
            alt=""
            className="h-11 w-11 rounded-full border-2 border-gold/40 object-cover"
          />
        ) : (
          <div className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-gold/40 bg-[var(--gradient-gold)] font-display text-sm text-foreground shadow-[var(--shadow-gold)]">
            {initials(t.author_name) || "💛"}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="truncate font-display text-sm text-foreground">
            — {t.author_name}
          </div>
          {t.author_role && (
            <div className="truncate text-[11px] uppercase tracking-wider text-foreground/60">
              {t.author_role}
            </div>
          )}
        </div>
        {t.source && (
          <span className="rounded-full bg-foreground/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-foreground/60">
            {t.source}
          </span>
        )}
      </div>
    </div>
  );
}

function TestimonialsPage() {
  const fetchTestimonials = useServerFn(getPublishedTestimonials);
  const [testimonials, setTestimonials] = useState<PublicTestimonial[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState<(typeof SOURCE_TABS)[number]["key"]>("all");

  useEffect(() => {
    let cancel = false;
    fetchTestimonials()
      .catch(() => [])
      .then((t) => {
        if (cancel) return;
        setTestimonials(t);
        setLoaded(true);
      });
    return () => {
      cancel = true;
    };
  }, [fetchTestimonials]);

  const filtered = useMemo(() => {
    if (tab === "all") return testimonials;
    return testimonials.filter(
      (t) => (t.source || "").toLowerCase() === tab.toLowerCase(),
    );
  }, [testimonials, tab]);

  const counts = useMemo(() => {
    const map: Record<string, number> = { all: testimonials.length };
    for (const t of testimonials) {
      const k = (t.source || "Community").trim();
      map[k] = (map[k] ?? 0) + 1;
    }
    return map;
  }, [testimonials]);

  return (
    <div className="relative">
      <div
        aria-hidden
        className="bg-blob-warm pointer-events-none absolute inset-x-0 top-0 -z-10 h-[600px] opacity-60"
      />
      <div className="mx-auto max-w-5xl px-4 py-8 sm:py-10">
        <header className="text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-background">
            <Sparkles className="h-3 w-3" /> Community voices
          </span>
          <h1 className="mt-4 font-display text-3xl leading-tight text-foreground sm:text-5xl">
            💛 What Our Community Is Saying
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-foreground/75 sm:text-base">
            Real voices from across Instagram, Facebook, and Trustpilot — survivors,
            supporters, and members of the Recovery Trauma™ community.
          </p>
        </header>

        <div
          className="mt-8 flex flex-wrap justify-center gap-2"
          role="tablist"
          aria-label="Filter testimonials by source"
        >
          {SOURCE_TABS.map((s) => {
            const active = tab === s.key;
            const count = counts[s.key] ?? 0;
            return (
              <button
                key={s.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(s.key)}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
                  active
                    ? "border-gold/60 bg-[var(--gradient-gold)] text-foreground shadow-[var(--shadow-gold)]"
                    : "border-gold/25 bg-card/80 text-foreground/75 hover:border-gold/45 hover:bg-gold-light/30"
                }`}
              >
                <span aria-hidden>{s.emoji}</span>
                {s.label}
                {loaded && (
                  <span className="rounded-full bg-foreground/10 px-2 py-0.5 text-[10px] font-semibold">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {!loaded && (
          <p className="mt-10 text-center text-sm text-foreground/60">
            Loading community voices…
          </p>
        )}

        {loaded && filtered.length === 0 && (
          <p className="mt-10 text-center text-sm text-foreground/60">
            No testimonials in this category yet.
          </p>
        )}

        {loaded && filtered.length > 0 && (
          <div className="mt-6">
            <Carousel
              opts={{ align: "start", loop: filtered.length > 1 }}
              className="w-full"
              aria-label="Community testimonials carousel"
            >
              <CarouselSwipeHint label="Swipe to read more" />
              <CarouselContent className="-ms-4">
                {filtered.map((t) => (
                  <CarouselItem
                    key={t.id}
                    className="ps-4 basis-4/5 sm:basis-1/2 lg:basis-1/3"
                  >
                    <TestimonialCard t={t} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              {filtered.length > 1 && (
                <>
                  <CarouselPrevious className="hidden sm:flex -start-3" />
                  <CarouselNext className="hidden sm:flex -end-3" />
                </>
              )}
            </Carousel>
          </div>
        )}

        <div className="mt-8 text-center">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-full border border-gold/40 bg-card px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-gold-light/40"
          >
            ← Back to home
          </Link>
        </div>
      </div>
      <LandingFooter />
    </div>
  );
}
